import { useEffect, useRef, useState, type ReactNode } from "react";
import gsap from "gsap";
import { ArrowDown, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useAnalytics } from "@/lib/analytics";
import { useAuth } from "@/components/auth/AuthProvider";
import {
  CURRENCY_PRICING,
  getCurrencySymbol,
  type SupportedCurrency,
} from "@/lib/currency-utils";
import {
  AddTeamSolidRoundedIcon,
  AiAudioSolidRoundedIcon,
  AnalyticsUpSolidRoundedIcon,
  BotSolidRoundedIcon,
  ChartBarFilledIcon,
  CommentFilledIcon,
  Contacts01SolidRoundedIcon,
  CustomerService01SolidStandardIcon,
  DashboardSquare02BulkRoundedIcon,
  GiftSolidRoundedIcon,
  HeartFilledIcon,
  Home01SolidRoundedIcon,
  InboxCheckSolidRoundedIcon,
  InstagramSolidRoundedIcon,
  Link02SolidRoundedIcon,
  MagicStoneSolidRoundedIcon,
  Mail01SolidRoundedIcon,
  News01SolidRoundedIcon,
  Rocket01SolidRoundedIcon,
  SentFilledIcon,
  ShieldFilledIcon,
  Suit01SolidRoundedIcon,
  Tag01SolidRoundedIcon,
  TranslateLanguageSolidRoundedIcon,
  UserAdd02SolidRoundedIcon,
  WorkflowSquare01SolidRoundedIcon,
} from "@/lib/custom-icons";
import { useTranslation } from "@/lib/i18n";
import PricingsPlansCta from "@/components/PricingsPlansCta";
import { LifetimeAccessPricing } from "@/components/life-time-access-pricing";
import { HugeiconsIcon } from "@hugeicons/react";
import MinusSignIcon from "@hugeicons/core-free-icons/MinusSignIcon";
import PlusSignIcon from "@hugeicons/core-free-icons/PlusSignIcon";
import type { Translations } from "@/lib/i18n/types";
import { cn } from "@/lib/utils";

const pricingFaqItems = [
  {
    icon: "🟨",
    q: "How much does Zapify cost?",
    a: "Zapify offers a 30-day Free Trial ($0, no credit card), Starter at $9.99/month, and Professional at $19.99/month. Both paid plans include unlimited automations, and yearly billing gives you 2 months free.",
  },
  {
    icon: "🎁",
    q: "Is Zapify free to try?",
    a: "Yes. Start with a 30-day free trial with no credit card required. You get 3 active automations, 1,000 AI comment replies, and 1 connected Instagram account to test Zapify before upgrading.",
  },
  {
    icon: "🚀",
    q: "Which Zapify plan should I choose?",
    a: "Starter is ideal for creators and solo businesses who want more automation and reply volume. Professional is designed for growing brands and teams that need more scale, multiple accounts, and priority support.",
  },
  {
    icon: "📊",
    q: "Does Zapify charge based on followers or contacts?",
    a: "No. Zapify uses simple flat-rate pricing and never charges based on follower count, audience size, or contacts.",
  },
  {
    icon: "⚡",
    q: "How does Zapify compare to other Instagram automation tools?",
    a: "Many automation platforms increase pricing as usage or audience grows. Zapify keeps pricing simple with predictable monthly plans built specifically for Instagram automation.",
  },
  {
    icon: "📅",
    q: "Do yearly plans save money?",
    a: "Yes. Annual billing gives you the equivalent of 2 months free compared to paying monthly.",
  },
  {
    icon: "🔄",
    q: "Can I upgrade, downgrade, or cancel anytime?",
    a: "Yes. Change or cancel your plan anytime from your dashboard. Your account remains active until the end of your billing period, and your settings stay saved.",
  },
  {
    icon: "🎧",
    q: "Is customer support included in every plan?",
    a: "Yes. All plans include support, with faster response times and priority assistance available on higher tiers.",
  },
  {
    icon: "🔍",
    q: "Are there any hidden fees or setup charges?",
    a: "No. There are no setup fees, onboarding charges, or hidden costs. You only pay for the plan you choose.",
  },
  {
    icon: "🛡️",
    q: "Is Zapify safe for my Instagram account?",
    a: "Yes. Zapify runs on Meta's official Instagram APIs and does not require password sharing or risky automation methods.",
  },
] as const;

function PricingFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div aria-label="Pricing FAQ" className="mx-auto w-full max-w-6xl py-12 sm:py-16 lg:py-20">
      {/* Title - Full width on top */}
      <div className="mb-8 text-center sm:mb-10">
        <h3 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-[2.5rem]">
          Pricing{" "}
          <span className="bg-primary-gradient bg-clip-text text-transparent">
            FAQ
          </span>
        </h3>
        <p className="mt-3 text-base text-muted-foreground sm:text-lg">
          Common questions about Zapify plans and billing
        </p>
      </div>

      {/* Q/A Grid - 2 columns (5|5) */}
      <div className="grid gap-4 sm:grid-cols-2 sm:gap-6">
          {/* Column 1 - First 5 questions */}
          <div className="space-y-3">
            {pricingFaqItems.slice(0, 5).map((item, index) => {
              const isOpen = openIndex === index;
              return (
                <div
                  key={item.q}
                  onMouseEnter={() => setOpenIndex(index)}
                  onMouseLeave={() => setOpenIndex(null)}
                  className="rounded-2xl border border-border/50 bg-card/90 px-4 py-3.5 transition-colors sm:px-6 sm:py-4 dark:bg-card/80"
                >
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-3 text-left"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-base leading-none">{item.icon}</span>
                      <p className="text-base font-semibold leading-snug text-foreground sm:text-[1.05rem]">
                        {item.q}
                      </p>
                    </div>
                    <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground">
                      {isOpen ? (
                        <HugeiconsIcon icon={MinusSignIcon} size={16} color="currentColor" aria-hidden />
                      ) : (
                        <HugeiconsIcon icon={PlusSignIcon} size={16} color="currentColor" aria-hidden />
                      )}
                    </span>
                  </button>
                  <div
                    className={`grid transition-all duration-300 ${isOpen ? "mt-2.5 grid-rows-[1fr] opacity-100 sm:mt-3" : "grid-rows-[0fr] opacity-0"}`}
                  >
                    <p className="overflow-hidden pr-3 text-[13px] leading-relaxed text-muted-foreground sm:pr-8 sm:text-[0.95rem]">
                      {item.a}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Column 2 - Last 5 questions */}
          <div className="space-y-3">
            {pricingFaqItems.slice(5, 10).map((item, colIndex) => {
              const index = colIndex + 5;
              const isOpen = openIndex === index;
              return (
                <div
                  key={item.q}
                  onMouseEnter={() => setOpenIndex(index)}
                  onMouseLeave={() => setOpenIndex(null)}
                  className="rounded-2xl border border-border/50 bg-card/90 px-4 py-3.5 transition-colors sm:px-6 sm:py-4 dark:bg-card/80"
                >
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-3 text-left"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-base leading-none">{item.icon}</span>
                      <p className="text-base font-semibold leading-snug text-foreground sm:text-[1.05rem]">
                        {item.q}
                      </p>
                    </div>
                    <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground">
                      {isOpen ? (
                        <HugeiconsIcon icon={MinusSignIcon} size={16} color="currentColor" aria-hidden />
                      ) : (
                        <HugeiconsIcon icon={PlusSignIcon} size={16} color="currentColor" aria-hidden />
                      )}
                    </span>
                  </button>
                  <div
                    className={`grid transition-all duration-300 ${isOpen ? "mt-2.5 grid-rows-[1fr] opacity-100 sm:mt-3" : "grid-rows-[0fr] opacity-0"}`}
                  >
                    <p className="overflow-hidden pr-3 text-[13px] leading-relaxed text-muted-foreground sm:pr-8 sm:text-[0.95rem]">
                      {item.a}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
  );
}

export const PLAN_FEATURES = {
  free: {
    // AUTOMATION POWER
    activeAutomations: "3",
    instagramAccounts: "1",
    customTriggerWords: "Unlimited",
    instantDMReplies: "2,000",
    commentToDMAutomation: true,
    storyReplyAutomation: true,
    welcomeMessageAutomation: true,
    smartGreetingMessage: true,
    unlimitedPostsStories: false,
    flexiblePostTargeting: false,
    linkInDMCTAButtons: true,
    followGatedDMsLinks: true,
    instagramSafetyLimits: true,
    customFlowBuilder: false,
    // AI CAPABILITIES
    aiCommentReplies: "1,000",
    aiPoweredInstantReplies: true,
    aiTrainedOnBrand: true,
    // LEAD GENERATION
    storedContacts: "1,000",
    emailCaptureInsideDMs: false,
    // ANALYTICS & TEAM
    instagramInsights: true,
    monthlyPerformanceReports: false,
    teamMembers: "0",
    // SUPPORT & PLATFORM
    customerSupport: "Community",
    languageSupport: "10+",
    platformBranding: true,
  },
  starter: {
    // AUTOMATION POWER
    activeAutomations: "Unlimited",
    instagramAccounts: "3",
    customTriggerWords: "Unlimited",
    instantDMReplies: "Unlimited",
    commentToDMAutomation: true,
    storyReplyAutomation: true,
    welcomeMessageAutomation: true,
    smartGreetingMessage: true,
    unlimitedPostsStories: true,
    flexiblePostTargeting: true,
    linkInDMCTAButtons: true,
    followGatedDMsLinks: true,
    instagramSafetyLimits: true,
    customFlowBuilder: false,
    // AI CAPABILITIES
    aiCommentReplies: "10,000",
    aiPoweredInstantReplies: true,
    aiTrainedOnBrand: true,
    // LEAD GENERATION
    storedContacts: "Unlimited",
    emailCaptureInsideDMs: false,
    // ANALYTICS & TEAM
    instagramInsights: true,
    monthlyPerformanceReports: false,
    teamMembers: "3",
    // SUPPORT & PLATFORM
    customerSupport: "Chat support",
    languageSupport: "10+",
    platformBranding: false,
  },
  professional: {
    // AUTOMATION POWER
    activeAutomations: "Unlimited",
    instagramAccounts: "6",
    customTriggerWords: "Unlimited",
    instantDMReplies: "Unlimited",
    commentToDMAutomation: true,
    storyReplyAutomation: true,
    welcomeMessageAutomation: true,
    smartGreetingMessage: true,
    unlimitedPostsStories: true,
    flexiblePostTargeting: true,
    linkInDMCTAButtons: true,
    followGatedDMsLinks: true,
    instagramSafetyLimits: true,
    customFlowBuilder: true,
    // AI CAPABILITIES
    aiCommentReplies: "Unlimited",
    aiPoweredInstantReplies: true,
    aiTrainedOnBrand: true,
    // LEAD GENERATION
    storedContacts: "Unlimited",
    emailCaptureInsideDMs: true,
    // ANALYTICS & TEAM
    instagramInsights: true,
    monthlyPerformanceReports: true,
    teamMembers: "Unlimited",
    // SUPPORT & PLATFORM
    customerSupport: "Priority chat + calls",
    languageSupport: "10+",
    platformBranding: false,
  },
} as const;

const plans = [
  {
    name: "Free Trial",
    planId: "free" as const,
    price: 0,
    description: "Perfect to get started with Zapify.",
    featureData: PLAN_FEATURES.free,
    highlight: false,
    cta: "Start 30 Days Free Trial",
    metadata: {
      id: "free_trial",
      type: "free",
      monthlyPrice: 0,
      yearlyPrice: 0,
    },
  },
  {
    name: "Starter",
    planId: "starter" as const,
    price: 9.99,
    description: "Best for individual content creators.",
    featureData: PLAN_FEATURES.starter,
    highlight: false,
    cta: "Subscribe",
    metadata: {
      id: "prod_SX7C6fQpqDPi0S",
      type: "subscription",
      monthlyPrice: 9.99,
      yearlyPrice: 99.0,
      monthlyPriceId: "price_1RnnMgQuf5DD5NVM0tK8fnBS",
      yearlyPriceId: "price_1Ro76YQuf5DD5NVMoz5hjXjB",
    },
  },
  {
    name: "Professional",
    planId: "professional" as const,
    price: 14.99,
    description: "Best for startups and small brands.",
    featureData: PLAN_FEATURES.professional,
    highlight: true,
    cta: "Subscribe",
    metadata: {
      id: "prod_SX7KKxNttt5L97",
      type: "subscription",
      monthlyPrice: 19.99,
      yearlyPrice: 199.0,
      monthlyPriceId: "price_1RnnSNQuf5DD5NVMx6NnNRxf",
      yearlyPriceId: "price_1Ro8sBQuf5DD5NVMmDVEE9Eu",
    },
  },
] as const;

type PlanFeatureKey = keyof typeof PLAN_FEATURES.free;

// Comparison table structure with category headers
type CompareRow =
  | { type: "header"; label: string }
  | { type: "feature"; key: PlanFeatureKey };

const COMPARE_ROWS: CompareRow[] = [
  // AUTOMATION POWER
  { type: "header", label: "automationPower" },
  { type: "feature", key: "activeAutomations" },
  { type: "feature", key: "instagramAccounts" },
  { type: "feature", key: "customTriggerWords" },
  { type: "feature", key: "instantDMReplies" },
  { type: "feature", key: "commentToDMAutomation" },
  { type: "feature", key: "storyReplyAutomation" },
  { type: "feature", key: "welcomeMessageAutomation" },
  { type: "feature", key: "smartGreetingMessage" },
  { type: "feature", key: "unlimitedPostsStories" },
  { type: "feature", key: "flexiblePostTargeting" },
  { type: "feature", key: "linkInDMCTAButtons" },
  { type: "feature", key: "followGatedDMsLinks" },
  { type: "feature", key: "instagramSafetyLimits" },
  { type: "feature", key: "customFlowBuilder" },
  // AI CAPABILITIES
  { type: "header", label: "aiCapabilities" },
  { type: "feature", key: "aiCommentReplies" },
  { type: "feature", key: "aiPoweredInstantReplies" },
  { type: "feature", key: "aiTrainedOnBrand" },
  // LEAD GENERATION
  { type: "header", label: "leadGeneration" },
  { type: "feature", key: "storedContacts" },
  { type: "feature", key: "emailCaptureInsideDMs" },
  // ANALYTICS & TEAM
  { type: "header", label: "analyticsTeam" },
  { type: "feature", key: "instagramInsights" },
  { type: "feature", key: "monthlyPerformanceReports" },
  { type: "feature", key: "teamMembers" },
  // SUPPORT & PLATFORM
  { type: "header", label: "supportPlatform" },
  { type: "feature", key: "customerSupport" },
  { type: "feature", key: "languageSupport" },
  { type: "feature", key: "platformBranding" },
];

function compareFeatureLabel(key: PlanFeatureKey, pr: Translations["pricing"]): string {
  const labels: Record<PlanFeatureKey, string> = {
    // AUTOMATION POWER
    activeAutomations: pr.activeAutomations,
    instagramAccounts: pr.instagramAccounts,
    customTriggerWords: pr.customTriggerWords,
    instantDMReplies: pr.instantDMReplies,
    commentToDMAutomation: pr.commentToDMAutomation,
    storyReplyAutomation: pr.storyReplyAutomation,
    welcomeMessageAutomation: pr.welcomeMessageAutomation,
    smartGreetingMessage: pr.smartGreetingMessage,
    unlimitedPostsStories: pr.unlimitedPostsStories,
    flexiblePostTargeting: pr.flexiblePostTargeting,
    linkInDMCTAButtons: pr.linkInDMCTAButtons,
    followGatedDMsLinks: pr.followGatedDMsLinks,
    instagramSafetyLimits: pr.instagramSafetyLimits,
    customFlowBuilder: pr.customFlowBuilder,
    // AI CAPABILITIES
    aiCommentReplies: pr.aiCommentReplies,
    aiPoweredInstantReplies: pr.aiPoweredInstantReplies,
    aiTrainedOnBrand: pr.aiTrainedOnBrand,
    // LEAD GENERATION
    storedContacts: pr.storedContacts,
    emailCaptureInsideDMs: pr.emailCaptureInsideDMs,
    // ANALYTICS & TEAM
    instagramInsights: pr.instagramInsights,
    monthlyPerformanceReports: pr.monthlyPerformanceReports,
    teamMembers: pr.teamMembers,
    // SUPPORT & PLATFORM
    customerSupport: pr.customerSupport,
    languageSupport: pr.languageSupport,
    platformBranding: pr.platformBranding,
  };
  return labels[key];
}

function formatCompareString(val: string, pr: Translations["pricing"]): string {
  if (val === "Limited") return pr.limited;
  if (val === "Email") return pr.email;
  if (val === "Priority") return pr.priority;
  if (val === "Unlimited") return pr.unlimited;
  return val;
}

function CompareBoolCell({ value, reverse }: { value: boolean; reverse: boolean }) {
  const isGood = reverse ? !value : value;
  return (
    <div className="flex justify-center">
      {isGood ? (
        <Check className="size-4 text-foreground sm:size-[1.125rem]" strokeWidth={2.5} aria-hidden />
      ) : (
        <X className="size-4 text-muted-foreground sm:size-[1.125rem]" strokeWidth={2.5} aria-hidden />
      )}
    </div>
  );
}

/** One full-height strip behind the Professional column (matches Dashboard `bg-primary-gradient` + glass). */
function PricingCompareProColumnBackdrop() {
  return (
    <div
      aria-hidden
      className={cn(
        "pointer-events-none absolute inset-y-0 right-0 z-0 min-w-[7rem] w-[22%]",
        "overflow-hidden rounded-r-[14px] border-l border-white/40 shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] dark:border-white/15 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]",
      )}
    >
      <div className="absolute inset-0 bg-primary-gradient opacity-[0.42] dark:opacity-[0.48]" />
      <div className="absolute inset-0 bg-white/45 backdrop-blur-2xl dark:bg-black/25" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/55 via-transparent to-white/20 dark:from-white/12 dark:via-transparent dark:to-transparent" />
    </div>
  );
}

function PricingCompareTable({ p, isYearly, currencySymbol, currency }: { p: Translations["pricing"]; isYearly: boolean; currencySymbol: string; currency: SupportedCurrency }) {
  return (
    <div id="pricing-compare" className="mx-auto mt-16 w-full max-w-5xl sm:mt-20">
      <div className="mb-8 text-center">
        <div className="mx-auto mb-3 inline-flex items-center justify-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-foreground dark:border-primary/20 dark:bg-primary/10">
          <span>{p.comparePlansEyebrow}</span>
        </div>
        <h3 className="text-balance text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
          Start Free,{" "}
          <span className="bg-primary-gradient bg-clip-text text-transparent">
            Scale when you're ready.
          </span>{" "}
          expires
        </h3>
        <p className="mx-auto mt-3 max-w-2xl text-pretty text-sm leading-relaxed text-muted-foreground sm:text-base sm:leading-relaxed">
          {p.comparePlansSubtitle}
        </p>
      </div>

      <div className="overflow-hidden rounded-2xl bg-card dark:bg-card/80">
        <div className="overflow-x-auto">
          <div className="relative min-w-[32rem]">
            <PricingCompareProColumnBackdrop />
            <table className="relative z-[1] w-full min-w-[32rem] table-fixed border-collapse text-sm">
              <colgroup>
                <col style={{ width: "34%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "22%" }} />
              </colgroup>
              <thead>
                <tr className="border-b border-border/50">
                  <th
                    scope="col"
                    className="min-w-0 bg-muted/50 px-4 py-3.5 text-left dark:bg-muted/20 sm:px-5 sm:py-4"
                  >
                    <span className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-wide text-foreground">
                      <span>{p.compareFeatureColumn}</span>
                      <ArrowDown className="size-4 shrink-0 text-muted-foreground" strokeWidth={2.5} aria-hidden />
                    </span>
                  </th>
                  {plans.map((plan) => (
                    <th
                      key={plan.planId}
                      scope="col"
                      className={cn(
                        "border-l border-border/[0.15] px-3 py-4 text-center sm:px-4 sm:py-5",
                        plan.highlight ? "bg-transparent" : "bg-muted/50 dark:bg-muted/20",
                      )}
                    >
                      <div className="flex flex-col items-center gap-1.5">
                        <span
                          className={cn(
                            "flex size-10 items-center justify-center rounded-xl",
                            plan.planId === "free" &&
                              "bg-red-500/10",
                            plan.planId === "starter" &&
                              "bg-sky-500/10",
                            plan.highlight &&
                              "bg-white/15",
                          )}
                        >
                          {plan.planId === "free" ? (
                            <GiftSolidRoundedIcon size={24} color="#dc2626" aria-hidden />
                          ) : plan.planId === "starter" ? (
                            <Rocket01SolidRoundedIcon size={24} color="#0284c7" aria-hidden />
                          ) : (
                            <Suit01SolidRoundedIcon
                              size={24}
                              color="currentColor"
                              className="text-primary drop-shadow-sm dark:text-primary-foreground"
                              aria-hidden
                            />
                          )}
                        </span>
                        <span className="text-sm font-bold tracking-tight text-foreground sm:text-base">
                          {plan.planId === "free" ? p.freeTrial : plan.planId === "starter" ? p.starter : p.professional}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {plan.planId === "free"
                            ? "Free"
                            : (() => {
                                const pricing = CURRENCY_PRICING[currency][plan.planId as "starter" | "professional"];
                                const price = isYearly ? pricing.yearly / 12 : pricing.monthly;
                                return `${currencySymbol}${price % 1 === 0 ? price.toFixed(0) : price.toFixed(2)}/mo`;
                              })()}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map((row, idx) => {
                  // Category header row
                  if (row.type === "header") {
                    const headerLabels: Record<string, string> = {
                      automationPower: p.automationPower,
                      aiCapabilities: p.aiCapabilities,
                      leadGeneration: p.leadGeneration,
                      analyticsTeam: p.analyticsTeam,
                      supportPlatform: p.supportPlatform,
                    };
                    return (
                      <tr key={`header-${row.label}`} className="border-b border-border/50">
                        <th
                          scope="row"
                          colSpan={4}
                          className="bg-white px-4 py-5 text-left dark:bg-black sm:px-5 sm:py-6"
                        >
                          <span className="flex items-center gap-2 text-base font-bold text-foreground">
                            <span className="h-4 w-1 rounded-full bg-primary/70" />
                            {headerLabels[row.label]}
                          </span>
                        </th>
                      </tr>
                    );
                  }

                  // Feature row
                  const key = row.key;
                  const rowBg =
                    idx % 2 === 1 ? "bg-muted/25 dark:bg-muted/10" : "bg-card dark:bg-card";
                  return (
                    <tr key={key} className="border-b border-border/30 last:border-b-0">
                      <th
                        scope="row"
                        className={cn("px-4 py-4 text-left sm:px-5 sm:py-5", rowBg)}
                      >
                        <span className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">
                            {compareFeatureLabel(key, p)}
                          </span>
                          {key === "customFlowBuilder" && (
                            <span className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                              {p.soonBadge}
                            </span>
                          )}
                          {key === "monthlyPerformanceReports" && (
                            <span className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                              {p.soonBadge}
                            </span>
                          )}
                        </span>
                      </th>
                      {plans.map((plan) => {
                        const raw = plan.featureData[key];
                        const isProCol = plan.planId === "professional";
                        return (
                          <td
                            key={`${plan.planId}-${String(key)}`}
                            className={cn(
                              "border-l border-border/[0.15] px-3 py-4 align-middle sm:px-4 sm:py-5",
                              isProCol ? "bg-primary/[0.02] dark:bg-primary/[0.03]" : rowBg,
                            )}
                          >
                            {typeof raw === "boolean" ? (
                              <CompareBoolCell value={raw} reverse={false} />
                            ) : (
                              <div
                                className={cn(
                                  "text-center text-xs font-semibold tabular-nums sm:text-sm",
                                  (raw === "0" || raw === "Community") && "text-muted-foreground",
                                  isProCol && "font-bold text-foreground",
                                )}
                              >
                                {formatCompareString(String(raw), p)}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

const FEATURE_ICON_SIZE = 18;

/** Featured plan: gradient frame padding (px) — inner radius = outer − pad */
const FEATURED_FRAME_PAD_PX = 4;
const FEATURED_OUTER_RADIUS_PX = 16;
const FEATURED_INNER_RADIUS_PX = FEATURED_OUTER_RADIUS_PX - FEATURED_FRAME_PAD_PX;

// const PLAN_PANDA_SRC: Record<"free" | "starter" | "professional", string> = {
//   free: "/panda/panda_free.webp",
//   starter: "/panda/panda_starter.webp",
//   professional: "/panda/professional_panda.webp",
// };

function PlanCardPanda({ planId }: { planId: "free" | "starter" | "professional" }) {
  void planId;
  return null;
  // return (
  //   <div className="-mb-2 z-0 flex w-full shrink-0 justify-center px-1 pb-0 leading-none ">
  //     <img
  //       src={PLAN_PANDA_SRC[planId]}
  //       alt=""
  //       width={400}
  //       height={400}
  //       className="block h-40 w-auto max-w-[min(100%,20rem)] translate-y-1.5 object-contain object-bottom sm:h-44 sm:translate-y-2 lg:h-48 lg:max-w-[min(100%,22rem)] lg:translate-y-2.5"
  //       loading="lazy"
  //       decoding="async"
  //     />
  //   </div>
  // );
}

/** Plan card header logo: same box, radius, and glyph size on every tier */
const PLAN_HEADER_SHELL = "flex size-14 shrink-0 items-center justify-center rounded-xl";
const PLAN_HEADER_ICON_PX = 28;

/** Subtle inner edge on icon / value wells inside cards */
const iconWellInset =
  "shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)] dark:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)]";

function PlanHeaderIcon({
  planId,
  isFeaturedPlan,
}: {
  planId: "free" | "starter" | "professional";
  isFeaturedPlan: boolean;
}) {
  if (planId === "free") {
    return (
      <div
        className={cn(
          PLAN_HEADER_SHELL,
          "border border-red-500/25 bg-red-500/10 ring-1 ring-red-500/15",
          iconWellInset,
        )}
      >
        <GiftSolidRoundedIcon
          size={PLAN_HEADER_ICON_PX}
          color="#dc2626"
          className="shrink-0"
          aria-hidden
        />
      </div>
    );
  }
  if (planId === "starter") {
    return (
      <div
        className={cn(
          PLAN_HEADER_SHELL,
          "border border-sky-500/25 bg-sky-500/10 ring-1 ring-sky-500/15",
          iconWellInset,
        )}
      >
        <Rocket01SolidRoundedIcon
          size={PLAN_HEADER_ICON_PX}
          color="#0284c7"
          className="shrink-0"
          aria-hidden
        />
      </div>
    );
  }
  if (planId === "professional" && isFeaturedPlan) {
    return (
      <div
        className={cn(
          PLAN_HEADER_SHELL,
          "bg-primary-gradient text-white shadow-md shadow-primary/25 ring-1 ring-white/25",
          iconWellInset,
        )}
      >
        <Suit01SolidRoundedIcon
          size={PLAN_HEADER_ICON_PX}
          color="#ffffff"
          className="shrink-0 drop-shadow-sm"
          aria-hidden
        />
      </div>
    );
  }
  return (
    <div
      className={cn(
        PLAN_HEADER_SHELL,
        "border border-border/50 bg-muted/80 ring-1 ring-border/30",
        iconWellInset,
      )}
    >
      <Suit01SolidRoundedIcon
        size={PLAN_HEADER_ICON_PX}
        color="currentColor"
        className="shrink-0 text-muted-foreground"
        aria-hidden
      />
    </div>
  );
}

type Plan = (typeof plans)[number];

function FeatureRow({
  label,
  value,
  icon,
  reverseBoolean = false,
}: {
  label: string;
  value: string | boolean;
  icon: React.ReactNode;
  reverseBoolean?: boolean;
}) {
  let content: ReactNode;
  let isPositive = true;

  if (typeof value === "boolean") {
    if (value) {
      content = <Check className="size-3" strokeWidth={2.5} />;
      isPositive = !reverseBoolean;
    } else {
      content = <X className="size-3" strokeWidth={2.5} />;
      isPositive = reverseBoolean;
    }
  } else {
    content = <span className="text-xs font-semibold tabular-nums">{value}</span>;
    isPositive = value !== "0" && value !== "Limited";
  }

  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-3 text-foreground">
        <span
          className={cn(
            "flex size-8 shrink-0 items-center justify-center rounded-lg border border-border/45 bg-muted/60 text-muted-foreground",
            iconWellInset,
          )}
        >
          {icon}
        </span>
        <span className="truncate text-sm font-medium">{label}</span>
      </div>
      <div
        className={cn(
          "flex h-7 min-w-7 shrink-0 items-center justify-center rounded-md border border-border/40 px-2 text-xs",
          isPositive ? "bg-muted/70 text-foreground" : "bg-muted/40 text-muted-foreground",
          iconWellInset,
        )}
      >
        {content}
      </div>
    </div>
  );
}

function PlanCard({
  plan,
  isYearly,
  currency = "usd",
}: {
  plan: Plan;
  isYearly: boolean;
  currency?: SupportedCurrency;
}) {
  const { track } = useAnalytics();
  const { openAuth } = useAuth();
  const { translations: t } = useTranslation();
  const metadata = plan.metadata as {
    id?: string;
    type?: string;
    monthlyPrice?: number;
    yearlyPrice?: number;
    monthlyPriceId?: string;
    yearlyPriceId?: string;
  };

  const planNameMap: Record<string, string> = {
    "Free Trial": t.pricing.freeTrial,
    Starter: t.pricing.starter,
    Professional: t.pricing.professional,
  };
  const planDescMap: Record<string, string> = {
    "Free Trial": t.pricing.freeTrialDesc,
    Starter: t.pricing.starterDesc,
    Professional: t.pricing.professionalDesc,
  };
  const translatedName = planNameMap[plan.name] ?? plan.name;
  const translatedDesc = planDescMap[plan.name] ?? plan.description;
  const translatedCta =
    plan.cta === "Subscribe" ? t.pricing.subscribe : t.pricing.startFreeTrial;
  const currencySymbol = getCurrencySymbol(currency);

  const getPlanPricing = () => {
    if (metadata?.type === "free") {
      return { monthly: 0, yearly: 0 };
    }
    const planKey = plan.name.toLowerCase() as "starter" | "professional";
    if (planKey === "starter" || planKey === "professional") {
      return CURRENCY_PRICING[currency][planKey];
    }
    return {
      monthly: metadata?.monthlyPrice ?? plan.price,
      yearly: metadata?.yearlyPrice ?? 0,
    };
  };

  const pricing = getPlanPricing();
  const monthlyPrice = pricing.monthly;
  const yearlyPrice = pricing.yearly;
  const displayPrice = isYearly ? yearlyPrice / 12 : monthlyPrice;
  const unit = t.pricing.perMonth;
  const isBestSeller = plan.highlight;
  const flipPaidPlan = plan.planId === "starter" || plan.planId === "professional";

  const cardSoftShadow = "shadow-[var(--soft-shadow)]";

  const cardInner = (
    <div
      className={cn(
        "relative z-10 flex h-full flex-col border bg-card p-6 sm:p-7",
        cardSoftShadow,
        isBestSeller ? "border-transparent" : "rounded-2xl border-border/50",
      )}
      style={isBestSeller ? { borderRadius: FEATURED_INNER_RADIUS_PX } : undefined}
      data-product-id={metadata?.id ?? undefined}
      data-price-id={isYearly ? metadata?.yearlyPriceId : metadata?.monthlyPriceId}
    >
      <div className="mb-6 flex items-start gap-3">
        <PlanHeaderIcon planId={plan.planId} isFeaturedPlan={isBestSeller} />
        <div className="min-w-0 pt-0.5">
          <h3 className="text-lg font-bold tracking-tight text-foreground">{translatedName}</h3>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{translatedDesc}</p>
        </div>
      </div>

      <div className="mb-6 flex min-h-[3rem] flex-wrap items-end justify-between gap-3">
        {metadata?.type === "free" ? (
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-3xl font-black tracking-tight text-foreground">{t.pricing.free}</span>
            <span className="rounded-full border border-border/45 bg-muted/50 px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
              {t.pricing.noCreditCard}
            </span>
          </div>
        ) : (
          <div className="flex flex-wrap items-end gap-1">
            <span className="text-3xl font-black tracking-tight text-foreground tabular-nums">
              {currencySymbol}
              {Math.floor(displayPrice ?? 0)}
              <span className="text-lg">.{(displayPrice ?? 0).toFixed(2).split(".")[1]}</span>
            </span>
            <span className="pb-1 text-sm text-muted-foreground">{unit}</span>
          </div>
        )}

        {isYearly && monthlyPrice > 0 && yearlyPrice > 0 && metadata?.type !== "free" ? (
          <div className="text-right text-xs leading-snug text-muted-foreground">
            <div>
              ({currencySymbol}
              {yearlyPrice}
              /year)
            </div>
            <div className="mt-0.5 font-medium text-foreground">
              {t.pricing.save}{" "}
              {Math.round(((monthlyPrice * 12 - yearlyPrice) / (monthlyPrice * 12)) * 100)}%
            </div>
          </div>
        ) : null}
      </div>

      <div className="mb-6">
        <Button
          variant={isBestSeller && metadata?.type !== "free" ? "gradient-brand" : "outline"}
          className={cn(
            "h-11 w-full rounded-full text-sm font-semibold shadow-none",
            isBestSeller && metadata?.type !== "free"
              ? "border-0 text-primary-foreground hover:text-primary-foreground"
              : "border-0 bg-black text-white hover:bg-neutral-900 hover:text-white",
          )}
          aria-label={
            metadata?.type === "free" ? `Start ${plan.name}` : `Subscribe to ${plan.name}`
          }
          onClick={() => {
            track("pricing_plan_selected", {
              plan_name: plan.name,
              plan_id: metadata?.id,
              plan_type: metadata?.type,
              billing_period: isYearly ? "yearly" : "monthly",
              price: isYearly ? metadata?.yearlyPrice : metadata?.monthlyPrice,
              section: "pricing_page",
            });
            openAuth();
          }}
        >
          {translatedCta}
        </Button>
      </div>

      <div className="space-y-2 pt-1">
        <FeatureRow
          label={t.pricing.activeAutomations}
          value={plan.featureData.activeAutomations}
          icon={<BotSolidRoundedIcon size={FEATURE_ICON_SIZE} color="currentColor" />}
        />
        <FeatureRow
          label={t.pricing.aiCommentReplies}
          value={plan.featureData.aiCommentReplies}
          icon={<AiAudioSolidRoundedIcon size={FEATURE_ICON_SIZE} color="currentColor" />}
        />
        <FeatureRow
          label={t.pricing.instagramAccounts}
          value={plan.featureData.instagramAccounts}
          icon={<InstagramSolidRoundedIcon size={FEATURE_ICON_SIZE} color="currentColor" />}
        />
        <FeatureRow
          label={t.pricing.teamMembers}
          value={plan.featureData.teamMembers}
          icon={<AddTeamSolidRoundedIcon size={FEATURE_ICON_SIZE} color="currentColor" />}
        />
        <FeatureRow
          label={t.pricing.emailCaptureInsideDMs}
          value={plan.featureData.emailCaptureInsideDMs}
          icon={<Mail01SolidRoundedIcon size={FEATURE_ICON_SIZE} color="currentColor" />}
        />
        <FeatureRow
          label={t.pricing.customerSupport}
          value={plan.featureData.customerSupport}
          icon={<CustomerService01SolidStandardIcon size={FEATURE_ICON_SIZE} color="currentColor" />}
        />
        <FeatureRow
          label={t.pricing.platformBranding}
          value={plan.featureData.platformBranding}
          icon={<Tag01SolidRoundedIcon size={FEATURE_ICON_SIZE} color="currentColor" />}
        />
      </div>
    </div>
  );

  if (isBestSeller) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <PlanCardPanda planId={plan.planId} />
        <div
          data-pricing-card
          data-plan-flip={flipPaidPlan ? "true" : undefined}
          className={cn(
            "relative z-10 min-h-0 flex-1 overflow-visible bg-primary-gradient shadow-[var(--soft-shadow-lg)] ring-1 ring-primary/20",
            flipPaidPlan && "[transform-style:preserve-3d] [backface-visibility:hidden]",
          )}
          style={{
            borderRadius: FEATURED_OUTER_RADIUS_PX,
            padding: FEATURED_FRAME_PAD_PX,
          }}
        >
          <div className="pointer-events-none absolute left-6 top-0 z-20 -translate-y-1/2">
            <span className="pointer-events-auto inline-flex rounded-full bg-primary-gradient px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-foreground shadow-sm shadow-primary/25 ring-1 ring-white/20">
              {t.pricing.bestSeller}
            </span>
          </div>
          {cardInner}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PlanCardPanda planId={plan.planId} />
      <div
        data-pricing-card
        data-plan-flip={flipPaidPlan ? "true" : undefined}
        className={cn(
          "relative z-10 min-h-0 flex-1",
          flipPaidPlan && "[transform-style:preserve-3d] [backface-visibility:hidden]",
        )}
      >
        {cardInner}
      </div>
    </div>
  );
}

export type PricingsProps = {
  countryCode?: string;
  currency?: SupportedCurrency;
};

export default function Pricings({ countryCode: _countryCode = "US", currency = "usd" }: PricingsProps) {
  const { track } = useAnalytics();
  const { openAuth } = useAuth();
  const [isYearly, setIsYearly] = useState(false);
  const { translations: t } = useTranslation();
  const cardsGridRef = useRef<HTMLDivElement>(null);
  const skipFirstFlipRef = useRef(true);
  const currencySymbol = getCurrencySymbol(currency);

  const emitToggle = (checked: boolean) => {
    track("pricing_toggle_changed", {
      billing_period: checked ? "yearly" : "monthly",
      section: "pricing_page",
    });
  };

  useEffect(() => {
    if (skipFirstFlipRef.current) {
      skipFirstFlipRef.current = false;
      return;
    }
    const paid = cardsGridRef.current?.querySelectorAll("[data-pricing-card][data-plan-flip]");
    if (!paid?.length) return;
    gsap.killTweensOf(paid);
    gsap.fromTo(
      paid,
      {
        rotationY: 90,
        transformOrigin: "center center",
        force3D: true,
      },
      {
        rotationY: 0,
        duration: 0.34,
        stagger: 0.025,
        ease: "power3.inOut",
        force3D: true,
        clearProps: "transform",
      },
    );
  }, [isYearly]);

  return (
    <section id="pricing" className="relative w-full scroll-mt-28">
      <div className="relative mx-auto w-full max-w-7xl container-padding py-12 sm:py-16 lg:py-20">
        <h1 className="sr-only">Zapify Pricing Plans - AI-Powered Instagram Automation</h1>

        <header className="mx-auto mb-10 max-w-2xl text-center sm:mb-14">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground sm:text-sm">
            {t.nav.pricing}
          </p>
          <h2 className="mt-3 text-balance text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-[2.5rem] lg:leading-tight">
            {t.pricing.headingTitle}{" "}
            <span className="bg-primary-gradient bg-clip-text text-transparent">
              {t.pricing.headingHighlight}
            </span>
          </h2>
          <p className="mt-4 text-pretty text-base leading-relaxed text-muted-foreground sm:text-lg">
            {t.pricing.headingDescription}
          </p>
        </header>

        <div className="mx-auto mb-10 flex justify-center sm:mb-12">
          <div className="flex flex-col-reverse items-center justify-center gap-3 sm:flex-row sm:flex-wrap sm:gap-4">
            <div className="flex items-center justify-center gap-4 rounded-full border border-neutral-200 bg-white/80 px-6 py-3 shadow-sm backdrop-blur-sm animate-in zoom-in-95 duration-500 delay-200 dark:border-neutral-700 dark:bg-neutral-800/80">
              <span
                className={cn(
                  "cursor-pointer text-base font-semibold transition-colors duration-200",
                  isYearly
                    ? "text-neutral-500 dark:text-neutral-400"
                    : "text-neutral-900 dark:text-neutral-100",
                )}
                onClick={() => {
                  if (!isYearly) return;
                  setIsYearly(false);
                  emitToggle(false);
                }}
              >
                {t.pricing.monthly}
              </span>
              <div className="scale-105 transition-transform active:scale-95">
                <Switch
                  checked={isYearly}
                  onCheckedChange={(checked) => {
                    if (checked === isYearly) return;
                    setIsYearly(checked);
                    emitToggle(checked);
                  }}
                  id="pricings-toggle"
                  aria-label={`${t.pricing.yearly} billing`}
                />
              </div>
              <span
                className={cn(
                  "cursor-pointer text-base font-semibold transition-colors duration-200",
                  !isYearly
                    ? "text-neutral-500 dark:text-neutral-400"
                    : "text-neutral-900 dark:text-neutral-100",
                )}
                onClick={() => {
                  if (isYearly) return;
                  setIsYearly(true);
                  emitToggle(true);
                }}
              >
                {t.pricing.yearly}
              </span>
            </div>

            <div className="relative overflow-visible">
              <img
                src="/arrowtoleft.png"
                alt=""
                width={40}
                height={40}
                className="absolute -bottom-4 -right-4 z-10 h-7 w-7 rotate-90 scale-x-[-1] sm:scale-x-100 sm:bottom-auto sm:-top-5 sm:-left-5 sm:-right-auto sm:rotate-[-5deg] sm:h-10 sm:w-10"
                loading="lazy"
                decoding="async"
              />
              <div
                className={cn(
                  "relative rotate-[5deg] rounded-full bg-primary-gradient p-[2px] transition-all duration-500",
                  isYearly ? "scale-100" : "scale-95",
                )}
              >
                <div className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-semibold sm:gap-2 sm:px-4 sm:py-1.5 sm:text-sm dark:bg-neutral-900">
                  {isYearly ? (
                    <img
                      src="https://fonts.gstatic.com/s/e/notoemoji/latest/1f389/512.webp"
                      alt=""
                      width={16}
                      height={16}
                      className="size-3.5 sm:size-4"
                      loading="lazy"
                    />
                  ) : null}
                  <span className="bg-primary-gradient bg-clip-text text-transparent">
                    {t.pricing.get2MonthsFree}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          ref={cardsGridRef}
          className="grid grid-cols-1 items-stretch gap-6 overflow-visible pt-8 sm:grid-cols-2 sm:gap-6 sm:pt-10 lg:grid-cols-3 lg:gap-8"
          style={{ perspective: "1400px" }}
        >
          {plans.map((plan) => (
            <PlanCard key={plan.name} plan={plan} isYearly={isYearly} currency={currency} />
          ))}
        </div>

        {/* Lifetime Access Card */}
        <div className="mt-12 sm:mt-16">
          <LifetimeAccessPricing
            spotsLeft={87}
            totalSpots={100}
            price={200}
            originalMonthlyPrice={19.99}
            onGetAccess={() => {
              track("pricing_plan_selected", {
                plan_name: "Lifetime Access",
                plan_id: "lifetime_deal",
                plan_type: "lifetime",
                billing_period: "yearly",
                price: 200,
                section: "pricing_page",
              });
              openAuth();
            }}
          />
        </div>

        <PricingCompareTable p={t.pricing} isYearly={isYearly} currencySymbol={currencySymbol} currency={currency} />
        <PricingFAQ />
        <PricingsPlansCta />
      </div>
    </section>
  );
}
